export type SettingsBag = Record<string, unknown>;

export const MAX_DEVICES = 16;
export const MAX_ROUTES = 16;

export type DeviceType =
  | 'sht21'
  | 'gpio_led'
  | 'gpio_buzzer'
  | 'gpio_relay'
  | 'gpio_input'
  | 'ws2812'
  | 'stacklight'
  | 'rgb_tft'
  | 'mqtt'
  | 'cellular'
  | 'lorawan'
  | 'zigbee'
  | 'modbus'
  | 'modbus_slave'
  | 'gps'
  | 'victron'
  | 'sml'
  | 'pn532'
  | 'vl53l0x';

export const DEVICE_TYPES: DeviceType[] = [
  'sht21',
  'gpio_led',
  'gpio_buzzer',
  'gpio_relay',
  'gpio_input',
  'ws2812',
  'stacklight',
  'rgb_tft',
  'mqtt',
  'cellular',
  'lorawan',
  'zigbee',
  'modbus',
  'modbus_slave',
  'gps',
  'victron',
  'sml',
  'pn532',
  'vl53l0x',
];

/** Bus/uplink — settings groups, not sensors. */
export const CONNECTION_DEVICE_TYPES: DeviceType[] = ['mqtt', 'cellular', 'lorawan', 'zigbee', 'modbus'];

/** Sensors, actuators, foreign slaves. */
export const PERIPHERY_DEVICE_TYPES: DeviceType[] = DEVICE_TYPES.filter(
  (type) => !CONNECTION_DEVICE_TYPES.includes(type),
);

export function isConnectionDeviceType(type: string): boolean {
  return (CONNECTION_DEVICE_TYPES as string[]).includes(type);
}

export type FlowCategory = 'controller' | 'bus' | 'instance' | 'source' | 'target' | 'dashboard' | 'logic' | 'trigger' | 'cloud';

const BUS_DEVICE_TYPES = new Set(['modbus', 'mqtt', 'cellular', 'lorawan', 'zigbee', 'i2c', 'uart', 'serial']);
export const TARGET_DEVICE_TYPES = new Set<string>(['ws2812', 'stacklight', 'gpio_led', 'gpio_buzzer', 'gpio_relay', 'rgb_tft']);
export const SOURCE_DEVICE_TYPES = new Set<string>([
  'sht21', 'gps', 'sml', 'pn532', 'vl53l0x', 'victron', 'modbus_slave', 'gpio_input',
]);

export const STACKLIGHT_STRIP_COUNT = 4;
export const STACKLIGHT_STRIP_LEDS = 16;
export const STACKLIGHT_CHANNEL_COUNT = 5;
const LEGACY_STACKLIGHT_STRIP_KEYS = ['north', 'east', 'south', 'west'] as const;

export function stacklightStripId(index: number): string {
  return `strip_${index + 1}`;
}

export function stacklightStripHandle(index: number): string {
  return `strip:${index}`;
}

export function parseStacklightStripHandle(handle: string | null | undefined): number | null {
  const match = /^strip:(\d+)$/.exec(String(handle || ''));
  if (!match) {
    return null;
  }
  const index = Number(match[1]);
  return Number.isInteger(index) && index >= 0 && index < STACKLIGHT_STRIP_COUNT ? index : null;
}

export function migrateStacklightDevice(device: GraphDevice): GraphDevice {
  if (device.type !== 'stacklight' && device.type !== 'signal_tower') {
    return device;
  }
  const next = clone(device);
  next.type = 'stacklight';
  if (next.id === 'tower') {
    next.id = 'stacklight';
  }
  const fromArray = Array.isArray(next.strips)
    ? (next.strips as unknown[]).map((item) => String(item || '').trim())
    : [];
  const fromLegacy = LEGACY_STACKLIGHT_STRIP_KEYS.map((key) => String(next[key] || '').trim());
  if (fromArray.some(Boolean)) {
    next.strips = Array.from({ length: STACKLIGHT_STRIP_COUNT }, (_, i) => fromArray[i] || '');
  } else if (fromLegacy.some(Boolean)) {
    next.strips = fromLegacy;
  }
  for (const key of LEGACY_STACKLIGHT_STRIP_KEYS) {
    delete next[key];
  }
  return next;
}

export function defaultStacklightStrips(): GraphDevice[] {
  return Array.from({ length: STACKLIGHT_STRIP_COUNT }, (_, index) => ({
    id: stacklightStripId(index),
    type: 'ws2812',
    enable: true,
    count: STACKLIGHT_STRIP_LEDS,
    order: 'GRB',
    brightness: 32,
  }));
}

export function canvasWs2812Id(node: { id: string; data: FlowNodeData }): string {
  return String(node.data.device?.id || (node.id.startsWith('d:') ? node.id.slice(2) : '')).trim();
}

export function ws2812StripIndex(id: string): number {
  const match = /^strip_(\d+)$/.exec(String(id || '').trim());
  return match ? Number(match[1]) : 0;
}

export function catalogWs2812Label(label: string | undefined, id: string): string {
  const stripped = String(label || '').replace(/\s*[·•]\s*GPIO\s+-?\d+\s*$/i, '').trim();
  if (stripped) {
    return stripped;
  }
  const index = ws2812StripIndex(id);
  return index ? `Strip ${index}` : (id || 'strip');
}

/** Canvas sticks are IDs only. Pin/count stay on the controller `devices[]` row. */
export function catalogWs2812Ref(id: string, enable = true): GraphDevice {
  return { id: id || 'strip', type: 'ws2812', enable: enable !== false };
}

export function catalogizeWs2812Nodes<T extends { id: string; data: FlowNodeData }>(nodes: T[]): T[] {
  return nodes.map((node) => {
    if (node.data.deviceType !== 'ws2812') {
      return node;
    }
    const id = canvasWs2812Id(node);
    return {
      ...node,
      data: {
        ...node.data,
        device: catalogWs2812Ref(id, node.data.device?.enable !== false),
      },
    };
  });
}

export function ws2812ControllerNode<T extends { id: string; data: FlowNodeData }>(
  strip: { id: string },
  nodes: T[],
  edges: Array<{ source: string; target: string; targetHandle?: string | null }>,
): T | null {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const ownerOf = (targetId: string): T | null => {
    const edge = edges.find((item) => (
      item.target === targetId
      && byId.get(item.source)?.data.kind === 'controller'
    ));
    return edge ? byId.get(edge.source) || null : null;
  };
  const direct = ownerOf(strip.id);
  if (direct) {
    return direct;
  }
  for (const edge of edges) {
    if (edge.source !== strip.id) {
      continue;
    }
    const target = byId.get(edge.target);
    if (!target || !isCatalogStacklight(target.data)) {
      continue;
    }
    const owner = ownerOf(target.id);
    if (owner) {
      return owner;
    }
  }
  return null;
}

export function ws2812Devices(devices: GraphDevice[]): GraphDevice[] {
  return devices.filter((item) => item.type === 'ws2812');
}

export function stacklightUsesWs2812(device: GraphDevice): boolean {
  return String(device.variant || 'modern') !== 'classic';
}

export function stacklightStripRefs(device: GraphDevice): string[] {
  const raw = Array.isArray(device.strips)
    ? (device.strips as unknown[]).map((item) => String(item || '').trim())
    : [];
  return Array.from({ length: STACKLIGHT_STRIP_COUNT }, (_, index) => raw[index] || '');
}

export function stacklightStripsReady(device: GraphDevice, existing: GraphDevice[]): boolean {
  if (device.type !== 'stacklight' || !stacklightUsesWs2812(device)) {
    return true;
  }
  const known = new Set(ws2812Devices(existing).map((item) => item.id));
  const refs = stacklightStripRefs(device);
  return refs.every((id) => id && known.has(id)) && new Set(refs).size === STACKLIGHT_STRIP_COUNT;
}

export function stacklightChannelRefs(device: GraphDevice): string[] {
  const raw = Array.isArray(device.channels)
    ? (device.channels as unknown[]).map((item) => String(item || '').trim())
    : [];
  return Array.from({ length: STACKLIGHT_CHANNEL_COUNT }, (_, index) => raw[index] || '');
}

/** Actuator endpoints classic stacklight can drive: relay.1 … led. */
export function actuatorChannelOptions(devices: GraphDevice[]): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = [];
  for (const device of devices) {
    if (device.type === 'gpio_relay') {
      const count = Math.min(8, Math.max(0, Number(device.count) || (Array.isArray(device.pins) ? device.pins.length : 0) || 4));
      for (let i = 1; i <= count; i += 1) {
        const id = `${device.id}.${i}`;
        out.push({ id, label: id });
      }
    }
    if (device.type === 'gpio_led') {
      out.push({ id: device.id, label: device.id });
    }
  }
  return out;
}

export function stacklightChannelsReady(device: GraphDevice, existing: GraphDevice[]): boolean {
  if (device.type !== 'stacklight' && device.type !== 'stacklight_legacy') {
    return true;
  }
  if (device.type === 'stacklight' && stacklightUsesWs2812(device)) {
    return true;
  }
  if (device.type === 'stacklight_legacy') {
    // Classic tile still creates the logic device; channels are filled when actuators exist.
    const known = actuatorChannelOptions(existing);
    if (!known.length) {
      return false;
    }
    return true;
  }
  const known = new Set(actuatorChannelOptions(existing).map((item) => item.id));
  const refs = stacklightChannelRefs(device);
  return refs.every((id) => id && known.has(id)) && new Set(refs).size === STACKLIGHT_CHANNEL_COUNT;
}

export function defaultLegacyActuators(): GraphDevice[] {
  return [];
}

export function stacklightCompanionStrips(existing: GraphDevice[], stacklight: GraphDevice): GraphDevice[] {
  const have = new Set(existing.map((item) => item.id).concat([stacklight.id]));
  const listed = Array.isArray(stacklight.strips)
    ? (stacklight.strips as unknown[]).map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const ids = listed.length ? listed.slice(0, STACKLIGHT_STRIP_COUNT) : defaultStacklightStrips().map((item) => item.id);
  const defaults = defaultStacklightStrips();
  return ids.flatMap((id, index) => {
    if (have.has(id)) {
      return [];
    }
    const fallback = defaults[index] || defaults[0];
    return [{ ...fallback, id }];
  });
}

export function isCatalogStacklight(data: Pick<FlowNodeData, 'deviceType' | 'signalName'>): boolean {
  return data.deviceType === 'stacklight' && data.signalName !== undefined;
}

export function flowCategory(data: Pick<FlowNodeData, 'kind' | 'deviceType' | 'device' | 'to' | 'signalName'>): FlowCategory {
  if (data.kind === 'controller') {
    return 'controller';
  }
  if (data.kind === 'trigger') {
    return 'trigger';
  }
  if (data.kind === 'transform') {
    return 'logic';
  }
  if (data.kind === 'cloudTransform') {
    return 'cloud';
  }
  if (data.kind === 'sink' && String(data.to || '').startsWith('dashboard.')) {
    return 'dashboard';
  }
  if (data.kind === 'sink') {
    return 'target';
  }
  if (data.kind === 'point' || data.kind === 'slave') {
    return 'source';
  }
  if (isCatalogStacklight(data)) {
    return 'instance';
  }
  const type = data.deviceType || data.device?.type || '';
  if (type === 'ws2812') {
    return 'instance';
  }
  if (type === 'modbus_slave') {
    return 'instance';
  }
  if (BUS_DEVICE_TYPES.has(type)) {
    return 'bus';
  }
  if (TARGET_DEVICE_TYPES.has(type)) {
    return 'target';
  }
  if (SOURCE_DEVICE_TYPES.has(type)) {
    return 'source';
  }
  return 'source';
}

export function isClimateDashboard(data: Pick<FlowNodeData, 'kind' | 'widgetType' | 'topicRh' | 'to'>): boolean {
  if (data.kind !== 'sink' || !String(data.to || '').startsWith('dashboard.')) {
    return false;
  }
  if (data.widgetType === 'stacklight' || data.widgetType === 'sml_meter') {
    return false;
  }
  if (data.widgetType === 'temperature' || data.topicRh) {
    return true;
  }
  return String(data.widgetType || 'temperature') === 'temperature';
}

export function isStacklightDashboard(data: Pick<FlowNodeData, 'kind' | 'widgetType' | 'to'>): boolean {
  return data.kind === 'sink'
    && String(data.to || '').startsWith('dashboard.')
    && data.widgetType === 'stacklight';
}

export function isSmlDashboard(data: Pick<FlowNodeData, 'kind' | 'widgetType' | 'to'>): boolean {
  return data.kind === 'sink'
    && String(data.to || '').startsWith('dashboard.')
    && data.widgetType === 'sml_meter';
}

/** Pins stay on the controller; the logical bus still appears on the canvas. */
export function isFlowCanvasDevice(device: GraphDevice): boolean {
  return device.type !== 'i2c' && device.type !== 'uart' && device.type !== 'mqtt';
}

export type GraphDevice = {
  id: string;
  type: string;
  enable?: boolean;
  [key: string]: unknown;
};

export type RouteTransform = {
  scale?: number;
  offset?: number;
  clampMin?: number;
  clampMax?: number;
  unit?: string;
};

export type GraphRoute = {
  id: string;
  enable?: boolean;
  when?: string;
  interval?: number;
  from: string;
  channel?: string;
  topic?: string;
  to?: string;
  transform?: RouteTransform;
};

export type FlowSchedule = {
  id?: string;
  every?: number;
  when?: string;
  emit: string;
};

export type FlowNodeKind = 'controller' | 'device' | 'slave' | 'point' | 'transform' | 'trigger' | 'cloudTransform' | 'sink';

export type FlowNodeLayout = {
  x: number;
  y: number;
  kind?: FlowNodeKind;
  label?: string;
  deviceType?: string;
  to?: string;
  when?: string;
  interval?: number;
  transform?: RouteTransform;
  busId?: string;
  pointKey?: string;
  layoutId?: string;
  widgetType?: string;
  topic?: string;
  topicRh?: string;
  topicPower?: string;
  topicReverse?: string;
  subscribe?: boolean;
  role?: string;
  ownerId?: string;
  signalName?: string;
  pin?: number;
  count?: number;
  strips?: string[];
  channels?: string[];
  generated?: boolean;
};

export type FlowLayout = {
  nodes?: Record<string, FlowNodeLayout>;
};

export type FlowNodeData = {
  kind: FlowNodeKind;
  label: string;
  deviceType?: string;
  device?: GraphDevice;
  busId?: string;
  slave?: Record<string, unknown>;
  point?: Record<string, unknown>;
  transform?: RouteTransform;
  when?: string;
  interval?: number;
  to?: string;
  layoutId?: string;
  widgetType?: string;
  topic?: string;
  topicRh?: string;
  topicPower?: string;
  topicReverse?: string;
  subscribe?: boolean;
  role?: string;
  ownerId?: string;
  signalName?: string;
  generated?: boolean;
};

export type FlowNode = {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: FlowNodeData;
};

export type FlowEdge = {
  id: string;
  source: string;
  sourceHandle?: string | null;
  target: string;
  targetHandle?: string | null;
  data?: { routeId?: string };
};

function asObject(value: unknown): SettingsBag {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as SettingsBag;
  }
  return {};
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function rawPoints(value: unknown): Record<string, unknown>[] {
  return asArray(value).map((item, index) => {
    const point = asObject(item);
    const key = String(point.key || `p${index}`);
    const idx = Number.isFinite(Number(point.index)) ? Number(point.index) : index;
    return { key, index: idx };
  });
}

function pointScaleToTransform(raw: SettingsBag): RouteTransform | undefined {
  const scale = raw.scale != null ? Number(raw.scale) : 1;
  const offset = raw.offset != null ? Number(raw.offset) : 0;
  const unit = typeof raw.unit === 'string' && raw.unit ? raw.unit : undefined;
  if ((Number.isFinite(scale) ? scale : 1) === 1 && (Number.isFinite(offset) ? offset : 0) === 0 && !unit) {
    return undefined;
  }
  const transform: RouteTransform = {};
  if (Number.isFinite(scale) && scale !== 1) {
    transform.scale = scale;
  }
  if (Number.isFinite(offset) && offset !== 0) {
    transform.offset = offset;
  }
  if (unit) {
    transform.unit = unit;
  }
  return Object.keys(transform).length ? transform : undefined;
}

function slaveFromLegacy(raw: SettingsBag, connection: string): GraphDevice {
  const id = String(raw.id || 'slave1');
  return {
    id,
    type: 'modbus_slave',
    enable: raw.enable !== false,
    name: raw.name || id,
    connection: String(raw.connection || connection || 'modbus'),
    unit: raw.unit ?? 1,
    fc: raw.fc ?? 3,
    address: raw.address ?? 0,
    count: raw.count ?? 1,
    points: rawPoints(raw.points),
  };
}

/** Nested type:modbus.slaves[] → sibling modbus_slave devices. Scale leaves the point. */
export function liftModbusSlaves(devices: GraphDevice[]): {
  devices: GraphDevice[];
  busIds: string[];
  scaleHints: { from: string; transform: RouteTransform }[];
} {
  const out: GraphDevice[] = [];
  const seen = new Set<string>();
  const busIds: string[] = [];
  const scaleHints: { from: string; transform: RouteTransform }[] = [];

  const takeSlave = (raw: SettingsBag, connection: string, busId?: string) => {
    const slave = slaveFromLegacy(raw, connection);
    if (seen.has(slave.id)) {
      return;
    }
    seen.add(slave.id);
    asArray(raw.points).forEach((item, index) => {
      const point = asObject(item);
      const transform = pointScaleToTransform(point);
      if (!transform) {
        return;
      }
      const key = String(point.key || `p${index}`);
      scaleHints.push({ from: `${slave.id}.${key}`, transform });
      if (busId) {
        scaleHints.push({ from: `${busId}.${slave.id}.${key}`, transform });
      }
    });
    out.push(slave);
  };

  for (const device of devices) {
    if (device.type === 'modbus_slave') {
      if (seen.has(device.id)) {
        continue;
      }
      seen.add(device.id);
      const next = clone(device);
      next.points = rawPoints(device.points);
      if (!next.connection) {
        next.connection = 'modbus';
      }
      delete next.slaves;
      out.push(next);
      continue;
    }
    if (device.type === 'modbus') {
      busIds.push(device.id);
      const next = clone(device);
      delete next.slaves;
      if (next.role !== 'slave') {
        next.role = 'master';
      }
      out.push(next);
      for (const raw of asArray(device.slaves)) {
        takeSlave(asObject(raw), String(device.id || 'modbus'), device.id);
      }
      continue;
    }
    out.push(clone(device));
  }
  return { devices: out, busIds, scaleHints };
}

function rewriteLiftedFrom(from: string, busIds: Set<string>, slaveIds: Set<string>): string {
  const parts = String(from || '').split('.').filter(Boolean);
  if (parts.length >= 2 && busIds.has(parts[0]) && slaveIds.has(parts[1])) {
    return parts.slice(1).join('.');
  }
  return from;
}

function nextId(prefix: string, existing: string[]): string {
  const used = new Set(existing);
  let n = 1;
  while (used.has(`${prefix}_${n}`) || used.has(prefix)) {
    n += 1;
    if (!used.has(`${prefix}_${n}`)) {
      return `${prefix}_${n}`;
    }
  }
  return used.has(prefix) ? `${prefix}_${n}` : prefix;
}

/** stacklight-legacy EVB opto inputs and button. Pins stay editable in settings. */
export const STACKLIGHT_LEGACY_INPUT_LINES = [
  { key: 'in1', pin: 1, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in2', pin: 2, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in3', pin: 3, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in4', pin: 15, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'button', pin: 9, mode: 'pullup', edge: 'change', inverted: true, enable: true },
];

/** @deprecated use STACKLIGHT_LEGACY_INPUT_LINES */
export const OLIMEX_GPIO_INPUT_LINES = STACKLIGHT_LEGACY_INPUT_LINES;

export function newDeviceId(type: string, devices: GraphDevice[]): string {
  const ids = devices.map((d) => d.id);
  const base = type === 'modbus' ? 'rs485' : type === 'modbus_slave' ? 'slave' : type === 'gpio_led' ? 'led' : type === 'gpio_buzzer' ? 'buzzer' : type === 'gpio_relay' ? 'relay' : type === 'gpio_input' ? 'inputs' : type === 'rgb_tft' ? 'display' : type === 'ws2812' ? 'strip' : type;
  if (!ids.includes(base)) {
    return base;
  }
  return nextId(base, ids);
}

export function defaultDevice(type: string, id: string): GraphDevice {
  if (type === 'vl53l0x') {
    return {
      id,
      type,
      enable: true,
      connection: 'i2c',
      sda_pin: 6,
      scl_pin: 7,
      xshut_pin: -1,
      address: 41,
    };
  }
  if (type === 'gpio_relay') {
    return { id, type, enable: true, count: 0, pins: [] };
  }
  if (type === 'gpio_led') {
    return { id, type, enable: true, pin: 8, inverted: true };
  }
  if (type === 'gpio_input') {
    return { id, type, enable: true, lines: STACKLIGHT_LEGACY_INPUT_LINES.map((line) => ({ ...line })) };
  }
  return { id, type, enable: true };
}

export function readDevices(settings: SettingsBag): GraphDevice[] {
  return asArray(settings.devices)
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => clone(item as GraphDevice));
}

export function normalizeRoute(route: GraphRoute): GraphRoute {
  const next = clone(route);
  if (!next.to) {
    if (next.channel === 'lorawan') {
      next.to = 'lorawan.uplink';
    } else if (next.channel === 'zigbee') {
      next.to = 'zigbee.uplink';
    } else if (next.topic) {
      next.to = String(next.topic).startsWith('mqtt.') ? String(next.topic) : `mqtt.${next.topic}`;
    } else if (next.channel === 'mqtt') {
      next.to = 'mqtt.{base}/status';
    }
  }
  return next;
}

export function readRoutes(settings: SettingsBag): GraphRoute[] {
  return asArray(settings.routes)
    .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
    .map((item) => normalizeRoute(item as GraphRoute));
}

/** Later entries with the same id replace earlier ones. Routes without an id are kept. */
export function mergeRoutes(base: GraphRoute[], extra: GraphRoute[]): GraphRoute[] {
  const incoming = extra.map((route) => normalizeRoute(route));
  const replace = new Set(incoming.map((route) => route.id).filter(Boolean));
  const kept = base.map((route) => normalizeRoute(route)).filter((route) => !route.id || !replace.has(route.id));
  return [...kept, ...incoming];
}

export function readFlowLayout(settings: SettingsBag): FlowLayout {
  const flow = asObject(settings.flow);
  const nodes = asObject(flow.nodes);
  const out: Record<string, FlowNodeLayout> = {};
  for (const [id, pos] of Object.entries(nodes)) {
    const p = asObject(pos);
    const snap: FlowNodeLayout = { x: Number(p.x) || 0, y: Number(p.y) || 0 };
    if (typeof p.kind === 'string') {
      snap.kind = p.kind as FlowNodeData['kind'];
    }
    if (typeof p.label === 'string' && p.label) {
      snap.label = p.label;
    }
    if (typeof p.deviceType === 'string' && p.deviceType) {
      snap.deviceType = p.deviceType;
    }
    if (typeof p.to === 'string' && p.to) {
      snap.to = p.to;
    }
    if (typeof p.when === 'string' && p.when) {
      snap.when = p.when;
    }
    if (p.interval != null && Number.isFinite(Number(p.interval))) {
      snap.interval = Number(p.interval);
    }
    if (p.transform && typeof p.transform === 'object' && !Array.isArray(p.transform)) {
      snap.transform = p.transform as RouteTransform;
    }
    if (typeof p.busId === 'string' && p.busId) {
      snap.busId = p.busId;
    }
    if (typeof p.pointKey === 'string' && p.pointKey) {
      snap.pointKey = p.pointKey;
    }
    if (typeof p.layoutId === 'string' && p.layoutId) {
      snap.layoutId = p.layoutId;
    }
    if (typeof p.widgetType === 'string' && p.widgetType) {
      snap.widgetType = p.widgetType;
    }
    if (typeof p.topic === 'string' && p.topic) {
      snap.topic = p.topic;
    }
    if (typeof p.topicRh === 'string' && p.topicRh) {
      snap.topicRh = p.topicRh;
    }
    if (typeof p.topicPower === 'string' && p.topicPower) {
      snap.topicPower = p.topicPower;
    }
    if (typeof p.topicReverse === 'string' && p.topicReverse) {
      snap.topicReverse = p.topicReverse;
    }
    if (p.subscribe === false) {
      snap.subscribe = false;
    }
    if (typeof p.role === 'string' && p.role) {
      snap.role = p.role;
    }
    if (typeof p.ownerId === 'string' && p.ownerId) {
      snap.ownerId = p.ownerId;
    }
    if (typeof p.signalName === 'string') {
      snap.signalName = p.signalName;
    }
    if (p.pin != null && Number.isFinite(Number(p.pin))) {
      snap.pin = Number(p.pin);
    }
    if (p.count != null && Number.isFinite(Number(p.count))) {
      snap.count = Number(p.count);
    }
    if (Array.isArray(p.strips)) {
      snap.strips = p.strips.map((item) => String(item || '').trim());
    }
    if (Array.isArray(p.channels)) {
      snap.channels = p.channels.map((item) => String(item || '').trim());
    }
    if (p.generated === true) {
      snap.generated = true;
    }
    out[id] = snap;
  }
  return { nodes: out };
}

export function synthesizeDevices(settings: SettingsBag): GraphDevice[] {
  const devices: GraphDevice[] = [];
  const mqtt = asObject(settings.mqtt);
  devices.push({
    id: 'mqtt',
    type: 'mqtt',
    enable: Boolean(mqtt.enable),
    server: mqtt.server ?? '',
    port: mqtt.port ?? 1883,
    user: mqtt.user ?? '',
    password: mqtt.password ?? '',
    topic: mqtt.topic ?? '',
  });
  const sensor = asObject(settings.sensor);
  devices.push({
    id: 'sht21',
    type: 'sht21',
    enable: Boolean(sensor.enable),
    sda_pin: sensor.sda_pin ?? 6,
    scl_pin: sensor.scl_pin ?? 7,
  });
  const modbus = asObject(settings.modbus);
  devices.push({
    id: 'rs485',
    type: 'modbus',
    enable: Boolean(modbus.enable),
    baud: modbus.baud ?? 9600,
    timeout_ms: modbus.timeout_ms ?? 1000,
    role: modbus.role === 'slave' ? 'slave' : 'master',
  });
  for (const raw of asArray(modbus.slaves)) {
    const slave = asObject(raw);
    const id = String(slave.id || `slave${devices.filter((d) => d.type === 'modbus_slave').length + 1}`);
    devices.push(slaveFromLegacy({ ...slave, id }, 'modbus'));
  }
  const led = asObject(settings.led);
  if (led.enable) {
    devices.push({
      id: 'led',
      type: 'gpio_led',
      enable: true,
      pin: led.pin ?? 0,
      inverted: Boolean(led.inverted),
    });
  }
  const buzzer = asObject(settings.buzzer);
  if (buzzer.enable) {
    devices.push({
      id: 'buzzer',
      type: 'gpio_buzzer',
      enable: true,
      pin: buzzer.pin ?? 0,
    });
  }
  const relay = asObject(settings.relay);
  if (relay.enable) {
    devices.push({
      id: 'relay',
      type: 'gpio_relay',
      enable: true,
      count: relay.count ?? 0,
      pins: clone(asArray(relay.pins)),
    });
  }
  const display = asObject(settings.display);
  if (display.enable) {
    devices.push({
      id: 'display',
      type: 'rgb_tft',
      enable: true,
      lvgl: Boolean(display.lvgl),
      panel: display.panel ?? 'matouch',
    });
  }
  const cellular = asObject(settings.cellular);
  if (cellular.enable) {
    devices.push({
      id: 'cellular',
      type: 'cellular',
      enable: true,
      uplink_mode: cellular.uplink_mode ?? 2,
      apn: cellular.apn ?? 'internet',
    });
  }
  const lorawan = asObject(settings.lorawan);
  if (lorawan.enable) {
    devices.push({
      id: 'lorawan',
      type: 'lorawan',
      enable: true,
      txinterval: lorawan.txinterval ?? 60,
    });
  }
  const zigbee = asObject(settings.zigbee);
  if (zigbee.enable) {
    devices.push({
      id: 'zigbee',
      type: 'zigbee',
      enable: true,
      wifiFallback: zigbee.wifiFallback ?? false,
      wifiFallbackAfter: zigbee.wifiFallbackAfter ?? 2,
      wifiFallbackSec: zigbee.wifiFallbackSec ?? 60,
      wifiFallbackLinkSec: zigbee.wifiFallbackLinkSec ?? 180,
    });
  }
  const gps = asObject(settings.gps);
  if (gps.enable) {
    devices.push({ id: 'gps', type: 'gps', enable: true });
  }
  const serial = asObject(settings.serial);
  if (serial.mode === 'victron') {
    devices.push({ id: 'victron', type: 'victron', enable: true });
  }
  if (serial.mode === 'sml') {
    devices.push({ id: 'sml', type: 'sml', enable: true });
  }
  return devices.slice(0, MAX_DEVICES);
}

export function synthesizeRoutes(_settings: SettingsBag): GraphRoute[] {
  return [];
}

export function projectDevicesToGroups(settings: SettingsBag): SettingsBag {
  const next: SettingsBag = { ...settings };
  const devices = readDevices(next);
  const mqttDev = devices.find((d) => d.type === 'mqtt');
  if (mqttDev) {
    next.mqtt = {
      ...asObject(next.mqtt),
      enable: Boolean(mqttDev.enable),
      server: mqttDev.server ?? '',
      port: mqttDev.port ?? 1883,
      user: mqttDev.user ?? '',
      password: mqttDev.password ?? '',
      topic: mqttDev.topic ?? '',
    };
  }
  const sht = devices.find((d) => d.type === 'sht21');
  if (sht) {
    next.sensor = {
      ...asObject(next.sensor),
      enable: Boolean(sht.enable),
      sda_pin: sht.sda_pin ?? 6,
      scl_pin: sht.scl_pin ?? 7,
    };
  }
  const nfc = devices.find((d) => d.type === 'pn532');
  const tof = devices.find((d) => d.type === 'vl53l0x');
  const i2cOn = Boolean(sht?.enable) || Boolean(nfc?.enable) || Boolean(tof?.enable);
  if (sht || nfc || tof) {
    const i2c = asObject(next.i2c);
    next.i2c = {
      ...i2c,
      enable: i2cOn,
      sda_pin: sht?.sda_pin ?? tof?.sda_pin ?? i2c.sda_pin ?? 6,
      scl_pin: sht?.scl_pin ?? tof?.scl_pin ?? i2c.scl_pin ?? 7,
    };
  }
  const bus = devices.find((d) => d.type === 'modbus');
  const slaveDevices = devices.filter((d) => d.type === 'modbus_slave');
  const prevModbus = asObject(next.modbus);
  if (bus || slaveDevices.length > 0) {
    next.modbus = {
      ...prevModbus,
      enable: bus ? Boolean(bus.enable) : true,
      baud: bus?.baud ?? prevModbus.baud ?? 9600,
      timeout_ms: bus?.timeout_ms ?? prevModbus.timeout_ms ?? 1000,
      role: bus?.role === 'slave' || prevModbus.role === 'slave' ? 'slave' : 'master',
      slaves: slaveDevices.map((s) => ({
        id: s.id,
        name: s.name || s.id,
        unit: s.unit ?? 1,
        fc: s.fc ?? 3,
        address: s.address ?? 0,
        count: s.count ?? 1,
        points: rawPoints(s.points),
      })),
    };
  }
  const led = devices.find((d) => d.type === 'gpio_led');
  if (led) {
    next.led = {
      ...asObject(next.led),
      enable: Boolean(led.enable),
      pin: led.pin ?? 0,
      inverted: Boolean(led.inverted),
    };
  }
  const buzzer = devices.find((d) => d.type === 'gpio_buzzer');
  if (buzzer) {
    next.buzzer = {
      ...asObject(next.buzzer),
      enable: Boolean(buzzer.enable),
      pin: buzzer.pin ?? 0,
    };
  }
  const relay = devices.find((d) => d.type === 'gpio_relay');
  if (relay) {
    next.relay = {
      ...asObject(next.relay),
      enable: Boolean(relay.enable),
      count: relay.count ?? 0,
      pins: clone(asArray(relay.pins)),
    };
  }
  const display = devices.find((d) => d.type === 'rgb_tft');
  if (display) {
    next.display = {
      ...asObject(next.display),
      enable: Boolean(display.enable),
      lvgl: Boolean(display.lvgl),
      panel: display.panel ?? 'matouch',
    };
  }
  const cellular = devices.find((d) => d.type === 'cellular');
  if (cellular) {
    next.cellular = {
      ...asObject(next.cellular),
      enable: Boolean(cellular.enable),
      uplink_mode: cellular.uplink_mode ?? 2,
      apn: cellular.apn ?? 'internet',
    };
  }
  const lorawan = devices.find((d) => d.type === 'lorawan');
  if (lorawan) {
    next.lorawan = {
      ...asObject(next.lorawan),
      enable: Boolean(lorawan.enable),
      txinterval: lorawan.txinterval ?? 60,
    };
  }
  const zigbee = devices.find((d) => d.type === 'zigbee');
  if (zigbee) {
    next.zigbee = {
      ...asObject(next.zigbee),
      enable: Boolean(zigbee.enable),
      wifiFallback: zigbee.wifiFallback ?? asObject(next.zigbee).wifiFallback,
      wifiFallbackAfter: zigbee.wifiFallbackAfter ?? asObject(next.zigbee).wifiFallbackAfter,
      wifiFallbackSec: zigbee.wifiFallbackSec ?? asObject(next.zigbee).wifiFallbackSec,
      wifiFallbackLinkSec: zigbee.wifiFallbackLinkSec ?? asObject(next.zigbee).wifiFallbackLinkSec,
    };
  }
  const gps = devices.find((d) => d.type === 'gps');
  if (gps) {
    next.gps = { ...asObject(next.gps), enable: Boolean(gps.enable) };
  }
  const victron = devices.find((d) => d.type === 'victron');
  const sml = devices.find((d) => d.type === 'sml');
  if (sml?.enable) {
    next.serial = { ...asObject(next.serial), mode: 'sml' };
  } else if (victron?.enable) {
    next.serial = { ...asObject(next.serial), mode: 'victron' };
  }
  return next;
}

const LEGACY_DEVICE_GROUPS = [
  'mqtt', 'sensor', 'modbus', 'led', 'buzzer', 'relay', 'display',
  'cellular', 'lorawan', 'zigbee', 'gps', 'serial',
] as const;

/** v2 singleton groups — not catalog flow files, which have routes/flow/cloud and no devices[]. */
function hasLegacyDeviceGroups(settings: SettingsBag): boolean {
  return LEGACY_DEVICE_GROUPS.some((key) => {
    const value = settings[key];
    return value != null && typeof value === 'object' && !Array.isArray(value);
  });
}

export function ensureGraph(settings: SettingsBag): SettingsBag {
  const next: SettingsBag = { ...settings };
  let devices = readDevices(next).map((device) => migrateStacklightDevice(device));
  if (devices.length === 0 && hasLegacyDeviceGroups(next)) {
    devices = synthesizeDevices(next);
  }
  const lifted = liftModbusSlaves(devices);
  next.devices = lifted.devices;
  const busIds = new Set(lifted.busIds);
  const slaveIds = new Set(lifted.devices.filter((d) => d.type === 'modbus_slave').map((d) => d.id));
  const applyHints = (routes: GraphRoute[]): GraphRoute[] => routes.map((route) => {
    const migrated = normalizeRoute(clone(route));
    migrated.from = rewriteLiftedFrom(migrated.from, busIds, slaveIds);
    if (!migrated.transform || identityTransform(migrated.transform)) {
      const hint = lifted.scaleHints.find((item) => item.from === route.from || item.from === migrated.from);
      if (hint) {
        migrated.transform = hint.transform;
      }
    }
    return migrated;
  });
  if (!Array.isArray(next.routes)) {
    next.routes = applyHints(synthesizeRoutes(next));
  } else {
    next.routes = applyHints(readRoutes(next));
  }
  if (!next.flow || typeof next.flow !== 'object' || Array.isArray(next.flow)) {
    next.flow = { nodes: {} };
  }
  return projectDevicesToGroups(next);
}

function identityTransform(t?: RouteTransform): boolean {
  if (!t) {
    return true;
  }
  const scale = t.scale ?? 1;
  const offset = t.offset ?? 0;
  return scale === 1 && offset === 0 && t.clampMin == null && t.clampMax == null && !t.unit;
}

const TYPED_SOURCE_IDS: Record<string, string> = {
  sht21: 'sht21',
  gps: 'gps',
  sml: 'sml',
  pn532: 'pn532',
  vl53l0x: 'vl53l0x',
  victron: 'victron',
};

const TYPED_SOURCE_PREFIXES = Object.keys(TYPED_SOURCE_IDS).sort((a, b) => b.length - a.length);

const SKIP_INFER_IDS = new Set(['mqtt', 'cellular', 'lorawan', 'zigbee', 'modbus', 'i2c', 'uart', 'rs485']);

function inferTypedDeviceType(id: string, keys: string[]): string | undefined {
  const exact = TYPED_SOURCE_IDS[id];
  if (exact) {
    return exact;
  }
  for (const prefix of TYPED_SOURCE_PREFIXES) {
    if (id === prefix || id.startsWith(prefix)) {
      return TYPED_SOURCE_IDS[prefix];
    }
  }
  const keySet = new Set(keys);
  if (id === 'inputs' || keySet.has('button') || keySet.has('in1')) {
    return 'gpio_input';
  }
  if (keySet.has('energy') || keySet.has('energy_reverse')) {
    return 'sml';
  }
  if (keySet.has('lat') || keySet.has('lon')) {
    return 'gps';
  }
  if (keySet.has('uid') || keySet.has('ndef')) {
    return 'pn532';
  }
  if (keySet.has('mm')) {
    return 'vl53l0x';
  }
  return undefined;
}

function deviceCanvasLabel(device: GraphDevice): string {
  if (device.type === 'modbus') {
    return 'Modbus';
  }
  if (device.type === 'mqtt') {
    return 'MQTT';
  }
  if (device.type === 'i2c') {
    return 'I2C';
  }
  if (device.type === 'uart') {
    return 'UART';
  }
  return device.id;
}

/** Catalog flows have no devices[]. Rebuild the Modbus bus, sensor, and points from `from` addresses. */
export function inferDevicesFromRoutes(routes: GraphRoute[], existing: GraphDevice[]): GraphDevice[] {
  const out = existing.map((device) => clone(device));
  const has = (id: string) => out.some((device) => device.id === id);
  const keysById = new Map<string, string[]>();
  const addKey = (id: string, key: string) => {
    const list = keysById.get(id) || [];
    if (!list.includes(key)) {
      list.push(key);
    }
    keysById.set(id, list);
  };
  routes.forEach((route) => {
    const from = String(route.from || '');
    const dot = from.indexOf('.');
    if (dot < 1) {
      return;
    }
    const id = from.slice(0, dot);
    const key = from.slice(dot + 1).split('.')[0];
    if (!id || !key || SKIP_INFER_IDS.has(id)) {
      return;
    }
    addKey(id, key);
  });
  Array.from(keysById.entries()).forEach(([id, keys]) => {
    if (has(id)) {
      const slave = out.find((device) => device.id === id && device.type === 'modbus_slave');
      if (!slave) {
        return;
      }
      const points = rawPoints(slave.points);
      const known = new Set(points.map((point) => String(point.key || '')));
      keys.forEach((key) => {
        if (!known.has(key)) {
          points.push({ key, index: points.length });
        }
      });
      slave.points = points;
      return;
    }
    const typed = inferTypedDeviceType(id, keys);
    if (typed) {
      const connection = typed === 'sml' || typed === 'gps' ? undefined : 'i2c';
      out.push({ id, type: typed, enable: true, ...(connection ? { connection } : {}) });
      return;
    }
    out.push({
      id,
      type: 'modbus_slave',
      enable: true,
      connection: 'modbus',
      points: keys.map((key, index) => ({ key, index })),
    });
  });
  if (out.some((device) => device.type === 'modbus_slave') && !out.some((device) => device.type === 'modbus')) {
    out.unshift({ id: 'rs485', type: 'modbus', enable: true });
  }
  return out;
}

function mqttTopicOfRoute(route: GraphRoute): string {
  const dest = String(route.to || '');
  if (dest.startsWith('mqtt.')) {
    return dest.slice('mqtt.'.length);
  }
  return String(route.topic || '').replace(/^mqtt\./i, '');
}

function transformLabel(transform?: RouteTransform): string {
  const scale = transform?.scale ?? 1;
  const unit = transform?.unit ? ` ${transform.unit}` : '';
  return `×${scale}${unit}`;
}

function busNode(type: 'i2c' | 'uart', label: string, layout: FlowLayout, y: number): FlowNode {
  const id = `b:${type}`;
  return {
    id,
    type: 'device',
    position: posFor(layout, id, { x: 40, y }),
    data: {
      kind: 'device',
      label,
      deviceType: type,
      ownerId: layout.nodes?.[id]?.ownerId,
      device: { id: type, type },
    },
  };
}

function attachConnectionBuses(
  nodes: FlowNode[],
  edges: FlowEdge[],
  devices: GraphDevice[],
  layout: FlowLayout,
): void {
  const pushEdge = (id: string, source: string, sourceHandle: string, target: string, targetHandle: string) => {
    if (edges.some((edge) => edge.id === id) || !nodes.some((node) => node.id === source) || !nodes.some((node) => node.id === target)) {
      return;
    }
    edges.push({ id, source, sourceHandle, target, targetHandle });
  };

  const ensure = (type: 'i2c' | 'uart', label: string, y: number): string => {
    const id = `b:${type}`;
    if (!nodes.some((node) => node.id === id)) {
      nodes.push(busNode(type, label, layout, y));
    }
    return id;
  };

  const modbus = devices.find((device) => device.type === 'modbus');
  devices.filter((device) => device.type === 'modbus_slave').forEach((slave) => {
    if (!modbus) {
      return;
    }
    pushEdge(`e-bus-${modbus.id}-${slave.id}`, `d:${modbus.id}`, 'bus', `d:${slave.id}`, 'in');
  });

  const i2cDevices = devices.filter((device) => {
    const connection = String(device.connection || '');
    return connection === 'i2c' || device.type === 'sht21' || device.type === 'pn532' || device.type === 'vl53l0x';
  });
  if (i2cDevices.length) {
    const busId = ensure('i2c', 'I2C', 40);
    i2cDevices.forEach((device) => {
      pushEdge(`e-bus-i2c-${device.id}`, busId, 'bus', `d:${device.id}`, 'bus');
    });
  }

  const uartDevices = devices.filter((device) => {
    const connection = String(device.connection || '');
    return device.type !== 'sml' && (connection === 'uart' || connection === 'serial');
  });
  if (uartDevices.length) {
    const busId = ensure('uart', 'UART', 160);
    uartDevices.forEach((device) => {
      pushEdge(`e-bus-uart-${device.id}`, busId, 'bus', `d:${device.id}`, 'bus');
    });
  }
}

function posFor(layout: FlowLayout, id: string, fallback: { x: number; y: number }): { x: number; y: number } {
  const snap = layout.nodes?.[id];
  return snap ? { x: snap.x, y: snap.y } : fallback;
}

function parseLvglDest(dest: string): { layoutId?: string; widgetId: string } | null {
  if (!dest.startsWith('lvgl.')) {
    return null;
  }
  const rest = dest.slice('lvgl.'.length);
  const dot = rest.indexOf('.');
  if (dot > 0) {
    return { layoutId: rest.slice(0, dot), widgetId: rest.slice(dot + 1) };
  }
  return { widgetId: rest };
}

function sinkLabel(dest: string): string {
  const lvgl = parseLvglDest(dest);
  if (lvgl) {
    return lvgl.layoutId ? `${lvgl.layoutId} · ${lvgl.widgetId}` : lvgl.widgetId;
  }
  if (dest.startsWith('dashboard.')) {
    return dest.slice('dashboard.'.length);
  }
  return dest.replace(/^lvgl\./, '');
}

function mqttTopicFrom(from: string, prev?: string): string {
  if (prev) {
    return prev;
  }
  const parts = from.split('.').filter(Boolean);
  if (parts[0] === 'gps' && parts[1]) {
    return `{base}/gps/${parts[1]}`;
  }
  if (parts[0] === 'sml' && parts[1]) {
    return `{base}/sml/${parts[1]}`;
  }
  if (parts[0] === 'pn532' && parts[1]) {
    return `{base}/nfc/${parts[1]}`;
  }
  if (parts[0] === 'vl53l0x' && parts[1]) {
    return `{base}/tof/${parts[1]}`;
  }
  if (parts.length >= 2) {
    return `{base}/${parts.join('/')}`;
  }
  return `{base}/${from || 'status'}`;
}

function isMqttDest(route: GraphRoute): boolean {
  const dest = String(route.to || '');
  return dest.startsWith('mqtt.') || dest.startsWith('mqtt:') || route.channel === 'mqtt' || Boolean(route.topic);
}

function isDashboardDest(dest: string): boolean {
  return dest.startsWith('dashboard.');
}

function normalizeMqttTopic(topic: string): string {
  return String(topic || '').replace(/^mqtt\./i, '').replace(/^\/+|\/+$/g, '');
}

function mqttTopicSlug(topic: string): string {
  return normalizeMqttTopic(topic)
    .replace(/[{}]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40) || 'topic';
}

function mqttTopicNodeId(topic: string, used: Set<string>): string {
  const base = `m:${mqttTopicSlug(topic)}`;
  let id = base;
  let n = 2;
  while (used.has(id)) {
    id = `${base}_${n}`;
    n += 1;
  }
  used.add(id);
  return id;
}

function mqttBrickTopic(node: FlowNode): string {
  return normalizeMqttTopic(String(node.data.topic || ''));
}

function findMqttBrick(nodes: FlowNode[], topic: string): FlowNode | undefined {
  const want = normalizeMqttTopic(topic);
  if (!want) {
    return undefined;
  }
  return nodes.find((node) => node.data.deviceType === 'mqtt' && mqttBrickTopic(node) === want);
}

function makeMqttBrick(id: string, topic: string, layout: FlowLayout, y: number, subscribe = true): FlowNode {
  return {
    id,
    type: 'device',
    position: posFor(layout, id, { x: 840, y }),
    data: {
      kind: 'device',
      label: topic || 'MQTT',
      deviceType: 'mqtt',
      topic: topic || undefined,
      subscribe,
      device: { id: 'mqtt', type: 'mqtt' },
    },
  };
}

function collectMqttTopics(settings: SettingsBag, nodes: FlowNode[], routes: GraphRoute[]): string[] {
  const topics: string[] = [];
  const add = (raw: string) => {
    const topic = normalizeMqttTopic(raw);
    if (topic && !topics.includes(topic)) {
      topics.push(topic);
    }
  };
  for (const route of routes) {
    if (isMqttDest(route)) {
      add(mqttTopicOfRoute(route));
    }
  }
  for (const node of nodes) {
    if (node.data.deviceType === 'mqtt' || isCatalogStacklight(node.data)) {
      add(String(node.data.topic || ''));
    }
  }
  for (const widget of asArray(asObject(settings.cloud).widgets).map((item) => asObject(item))) {
    add(String(widget.topic || ''));
    add(String(widget.topicRh || ''));
  }
  for (const route of asArray(asObject(settings.cloud).routes).map((item) => asObject(item))) {
    add(String(route.from || ''));
    add(mqttTopicOfRoute(route as GraphRoute));
  }
  return topics;
}

function ensureMqttTopicNodes(nodes: FlowNode[], settings: SettingsBag, routes: GraphRoute[], layout: FlowLayout): void {
  const topics = collectMqttTopics(settings, nodes, routes);
  const used = new Set(nodes.map((node) => node.id));
  const orphans = nodes.filter((node) => node.data.deviceType === 'mqtt' && !mqttBrickTopic(node));
  let orphanIndex = 0;
  topics.forEach((topic, index) => {
    const existing = findMqttBrick(nodes, topic);
    if (existing) {
      existing.data.subscribe = existing.data.subscribe !== false;
      if (!existing.data.label || existing.data.label === 'MQTT') {
        existing.data.label = topic;
      }
      return;
    }
    const orphan = orphans[orphanIndex];
    if (orphan) {
      orphanIndex += 1;
      orphan.data.topic = topic;
      orphan.data.subscribe = orphan.data.subscribe !== false;
      if (!orphan.data.label || orphan.data.label === 'MQTT') {
        orphan.data.label = topic;
      }
      return;
    }
    nodes.push(makeMqttBrick(mqttTopicNodeId(topic, used), topic, layout, 40 + index * 140, true));
  });
}

function nodeSnapshot(node: FlowNode): FlowNodeLayout {
  const snap: FlowNodeLayout = {
    x: node.position.x,
    y: node.position.y,
    kind: node.data.kind,
    label: node.data.label,
  };
  if (node.data.deviceType) {
    snap.deviceType = node.data.deviceType;
  }
  if (node.data.to) {
    snap.to = node.data.to;
  }
  if (node.data.when) {
    snap.when = node.data.when;
  }
  if (node.data.interval != null) {
    snap.interval = node.data.interval;
  }
  if (node.data.transform) {
    snap.transform = node.data.transform;
  }
  if (node.data.busId) {
    snap.busId = node.data.busId;
  }
  const pointKey = String(asObject(node.data.point).key || '');
  if (pointKey) {
    snap.pointKey = pointKey;
  }
  if (node.data.layoutId) {
    snap.layoutId = node.data.layoutId;
  }
  if (node.data.widgetType) {
    snap.widgetType = node.data.widgetType;
  }
  if (node.data.topic) {
    snap.topic = node.data.topic;
  }
  if (node.data.topicRh) {
    snap.topicRh = node.data.topicRh;
  }
  if (node.data.topicPower) {
    snap.topicPower = node.data.topicPower;
  }
  if (node.data.topicReverse) {
    snap.topicReverse = node.data.topicReverse;
  }
  if (node.data.deviceType === 'mqtt' && node.data.subscribe === false) {
    snap.subscribe = false;
  }
  if (node.data.kind === 'controller' && node.data.role) {
    snap.role = node.data.role;
  }
  if (node.data.ownerId) {
    snap.ownerId = node.data.ownerId;
  }
  if (node.data.signalName !== undefined) {
    snap.signalName = node.data.signalName;
  }
  if (node.data.deviceType === 'ws2812') {
    snap.label = catalogWs2812Label(node.data.label, canvasWs2812Id(node));
  }
  if (node.data.generated) {
    snap.generated = true;
  }
  return snap;
}

function isControllerSerialDevice(type?: string): boolean {
  return type === 'sml' || type === 'victron';
}

function serialEventFrom(from: string): boolean {
  const id = String(from || '').split('.')[0];
  const key = String(from || '').split('.')[1] || '';
  const typed = inferTypedDeviceType(id, key ? [key] : []);
  return isControllerSerialDevice(typed);
}

function isPollRoute(route: GraphRoute): boolean {
  if (serialEventFrom(String(route.from || ''))) {
    return false;
  }
  return String(route.when || 'interval') === 'interval';
}

function pollTargetOf(nodes: FlowNode[], source: { id: string; handle?: string }): { id: string; handle: string } {
  const node = nodes.find((item) => item.id === source.id);
  if (node && node.data.kind === 'point') {
    return { id: node.id, handle: 'poll' };
  }
  const pin = source.handle || '';
  if (!pin || pin === 'out' || pin === 'sample') {
    return { id: source.id, handle: 'poll' };
  }
  return { id: source.id, handle: `poll:${pin}` };
}

/** Do not resurrect v2 defaults that were painted onto every empty catalog canvas. */
function layoutRestoresDevice(id: string, deviceType: string, devices: GraphDevice[]): boolean {
  if (deviceType === 'sht21' || id === 'sht21') {
    return devices.some((device) => device.id === 'sht21' || device.type === 'sht21');
  }
  if (deviceType === 'modbus' || id === 'rs485') {
    return devices.some((device) => device.type === 'modbus' || device.id === id);
  }
  if (deviceType === 'i2c' || id === 'i2c') {
    return devices.some((device) => {
      const connection = String(device.connection || '');
      return connection === 'i2c' || device.type === 'sht21' || device.type === 'pn532' || device.type === 'vl53l0x';
    });
  }
  if (deviceType === 'uart' || id === 'uart') {
    return devices.some((device) => {
      const connection = String(device.connection || '');
      return connection === 'uart' || device.type === 'sml' || device.type === 'victron';
    });
  }
  return true;
}

export function settingsToFlow(settings: SettingsBag): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const graph = ensureGraph(settings);
  const routes = readRoutes(graph);
  const devices = inferDevicesFromRoutes(routes, readDevices(graph));
  const layout = readFlowLayout(graph);
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];

  devices.forEach((device, index) => {
    if (!isFlowCanvasDevice(device)) {
      return;
    }
    const id = `d:${device.id}`;
    const snap = layout.nodes?.[id];
    nodes.push({
      id,
      type: 'device',
      position: posFor(layout, id, { x: 80, y: 80 + index * 120 }),
      data: {
        kind: 'device',
        label: device.type === 'ws2812'
          ? catalogWs2812Label(snap?.label, device.id)
          : (snap?.label || deviceCanvasLabel(device)),
        deviceType: device.type,
        device: device.type === 'ws2812' ? catalogWs2812Ref(device.id, device.enable !== false) : device,
        ownerId: snap?.ownerId,
      },
    });
    if (device.type === 'modbus_slave') {
      asArray(device.points).forEach((praw, pIndex) => {
        const point = asObject(praw);
        const key = String(point.key || `p${pIndex}`);
        const pointId = `p:${device.id}:${key}`;
        nodes.push({
          id: pointId,
          type: 'point',
          position: posFor(layout, pointId, { x: 360, y: 80 + index * 120 + pIndex * 50 }),
          data: {
            kind: 'point',
            label: key,
            busId: device.id,
            slave: { id: device.id },
            point,
          },
        });
        edges.push({
          id: `e-pt-${device.id}-${key}`,
          source: id,
          sourceHandle: 'out',
          target: pointId,
          targetHandle: 'in',
        });
      });
    }
    if (device.type === 'gpio_input') {
      asArray(device.lines).forEach((praw, pIndex) => {
        const line = asObject(praw);
        const key = String(line.key || '').trim();
        if (!key) {
          return;
        }
        const pointId = `p:${device.id}:${key}`;
        nodes.push({
          id: pointId,
          type: 'point',
          position: posFor(layout, pointId, { x: 360, y: 80 + index * 120 + pIndex * 50 }),
          data: {
            kind: 'point',
            label: key,
            busId: device.id,
            point: line,
          },
        });
        edges.push({
          id: `e-pt-${device.id}-${key}`,
          source: id,
          sourceHandle: 'out',
          target: pointId,
          targetHandle: 'in',
        });
      });
    }
  });

  attachConnectionBuses(nodes, edges, devices, layout);

  routes.forEach((route, index) => {
    const dest = String(route.to || '');
    if (!dest.startsWith('lvgl.') && !dest.startsWith('modbus.hr') && !isDashboardDest(dest)) {
      return;
    }
    const parsed = parseLvglDest(dest);
    const sinkId = `k:${route.id}`;
    nodes.push({
      id: sinkId,
      type: 'sink',
      position: posFor(layout, sinkId, { x: 880, y: 80 + index * 90 }),
      data: {
        kind: 'sink',
        label: sinkLabel(dest),
        to: dest,
        layoutId: parsed?.layoutId,
      },
    });
  });

  routes.forEach((route, index) => {
    if (resolveFromNode(nodes, route.from)) {
      return;
    }
    const parts = String(route.from || '').split('.').filter(Boolean);
    if (parts.length < 2) {
      return;
    }
    const pointId = `p:${parts[0]}:${parts[1]}`;
    if (nodes.some((node) => node.id === pointId)) {
      return;
    }
    nodes.push({
      id: pointId,
      type: 'point',
      position: posFor(layout, pointId, { x: 280, y: 80 + index * 70 }),
      data: {
        kind: 'point',
        label: parts[1],
        busId: parts[0],
        slave: { id: parts[0] },
        point: { key: parts[1] },
      },
    });
  });

  Object.entries(layout.nodes || {}).forEach(([id, snap]) => {
    if (nodes.some((node) => node.id === id)) {
      return;
    }
    if (snap.kind === 'cloudTransform') {
      nodes.push({
        id,
        type: 'cloudTransform',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'cloudTransform',
          label: snap.label || 'Cloud',
          transform: snap.transform,
        },
      });
      return;
    }
    if (snap.kind === 'point' || id.startsWith('p:')) {
      const bits = id.split(':');
      const busId = snap.busId || bits[1] || 'src';
      const key = snap.pointKey || bits[2] || snap.label || 'value';
      nodes.push({
        id,
        type: 'point',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'point',
          label: snap.label || key,
          busId,
          slave: { id: busId },
          point: { key },
        },
      });
      return;
    }
    if (snap.kind === 'sink' || id.startsWith('k:')) {
      const to = snap.to || (snap.layoutId ? `lvgl.${snap.layoutId}.${snap.label || 'value'}` : `lvgl.${snap.label || 'value'}`);
      nodes.push({
        id,
        type: 'sink',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'sink',
          label: snap.label || sinkLabel(to),
          to,
          layoutId: snap.layoutId || parseLvglDest(to)?.layoutId,
          widgetType: snap.widgetType,
          topic: snap.topic,
          topicRh: snap.topicRh,
          topicPower: snap.topicPower,
          topicReverse: snap.topicReverse,
          signalName: snap.signalName,
        },
      });
      return;
    }
    if (snap.kind === 'trigger' || id.startsWith('t:')) {
      const event = snap.when === 'event';
      const interval = event ? undefined : (snap.interval ?? 60);
      nodes.push({
        id,
        type: 'trigger',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'trigger',
          label: snap.label || (event ? 'RX' : `${interval}s`),
          when: snap.when || 'interval',
          interval,
        },
      });
      return;
    }
    if (snap.kind === 'transform' || id.startsWith('x:')) {
      nodes.push({
        id,
        type: 'transform',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'transform',
          label: snap.label || '×1',
          transform: snap.transform || { scale: 1, offset: 0 },
        },
      });
      return;
    }
    if (snap.kind === 'controller' || id.startsWith('g:')) {
      nodes.push({
        id,
        type: 'controller',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'controller',
          label: snap.label || snap.role || 'Gerät',
          role: snap.role || snap.label,
        },
      });
      return;
    }
    if (snap.deviceType === 'modbus') {
      const busId = id.startsWith('d:') ? id.slice(2) : 'rs485';
      if (!devices.some((device) => device.type === 'modbus' && device.id === busId)) {
        return;
      }
      nodes.push({
        id,
        type: 'device',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'device',
          label: snap.label || 'Modbus',
          deviceType: 'modbus',
          ownerId: snap.ownerId,
          device: { id: busId, type: 'modbus', enable: true },
        },
      });
      return;
    }
    if (snap.deviceType === 'mqtt' || id === 'd:mqtt') {
      nodes.push({
        id,
        type: 'device',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'device',
          label: snap.label || snap.topic || 'MQTT',
          deviceType: 'mqtt',
          topic: snap.topic,
          subscribe: snap.subscribe !== false,
          device: { id: 'mqtt', type: 'mqtt' },
        },
      });
      return;
    }
    if (snap.deviceType === 'ws2812') {
      const busId = id.startsWith('d:') ? id.slice(2) : stacklightStripId(0);
      nodes.push({
        id,
        type: 'device',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'device',
          label: catalogWs2812Label(snap.label, busId),
          deviceType: 'ws2812',
          ownerId: snap.ownerId,
          device: catalogWs2812Ref(busId),
        },
      });
      return;
    }
    if (snap.deviceType === 'stacklight' || id.startsWith('s:')) {
      const signalName = snap.signalName ?? '';
      const strips = Array.isArray(snap.strips)
        ? snap.strips.map((item) => String(item || '').trim())
        : [];
      const channels = Array.isArray(snap.channels)
        ? snap.channels.map((item) => String(item || '').trim())
        : [];
      nodes.push({
        id,
        type: 'device',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'device',
          label: snap.label || signalName || 'stacklight',
          deviceType: 'stacklight',
          signalName,
          topic: snap.topic,
          ownerId: snap.ownerId,
          device: {
            id: signalName || id.replace(/^s:/, 'stacklight'),
            type: 'stacklight',
            ...(strips.some(Boolean) ? { strips } : {}),
            ...(channels.some(Boolean) ? { channels } : {}),
          },
        },
      });
      return;
    }
    if (snap.kind === 'device' || snap.deviceType || id.startsWith('d:') || id.startsWith('b:')) {
      const deviceType = snap.deviceType || (id.startsWith('b:') ? id.slice(2) : 'device');
      const busId = id.startsWith('d:') || id.startsWith('b:') ? id.slice(2) : id;
      if (!layoutRestoresDevice(busId, deviceType, devices)) {
        return;
      }
      nodes.push({
        id,
        type: 'device',
        position: posFor(layout, id, { x: snap.x, y: snap.y }),
        data: {
          kind: 'device',
          label: snap.label || busId,
          deviceType,
          ownerId: snap.ownerId,
          topic: snap.topic,
          device: { id: busId, type: deviceType, enable: true },
        },
      });
    }
  });

  ensureMqttTopicNodes(nodes, graph, routes, layout);
  attachControllerOwnerWires(nodes, edges);
  attachSerialReceiveTriggers(nodes, edges, routes, layout);
  attachStacklightMqttWires(nodes, edges);
  attachStacklightStripWires(nodes, edges, layout);

  const pollTriggers = new Map<number, string>();
  routes.forEach((route) => {
    if (!isPollRoute(route)) {
      return;
    }
    const source = resolveFromNode(nodes, route.from);
    if (!source) {
      return;
    }
    const interval = Number(route.interval) || 60;
    let triggerId = pollTriggers.get(interval);
    if (!triggerId) {
      triggerId = `t:${interval}`;
      pollTriggers.set(interval, triggerId);
      if (!nodes.some((node) => node.id === triggerId)) {
        nodes.push({
          id: triggerId,
          type: 'trigger',
          position: posFor(layout, triggerId, { x: 40, y: 40 + pollTriggers.size * 80 }),
          data: {
            kind: 'trigger',
            label: `${interval}s`,
            when: 'interval',
            interval,
          },
        });
      }
    }
    const poll = pollTargetOf(nodes, source);
    const edgeId = `e-poll-${interval}-${source.id}-${source.handle || 'out'}`;
    if (!edges.some((edge) => edge.id === edgeId)) {
      edges.push({
        id: edgeId,
        source: triggerId,
        sourceHandle: 'out',
        target: poll.id,
        targetHandle: poll.handle,
      });
    }
  });

  const xfByKey = new Map<string, string>();
  routes.forEach((route, index) => {
    const source = resolveFromNode(nodes, route.from);
    const sink = resolveToNode(nodes, route);
    if (!source || !sink) {
      return;
    }
    let fromId = source.id;
    let fromHandle = source.handle;
    if (!identityTransform(route.transform)) {
      const key = `${source.id}|${JSON.stringify(route.transform || {})}`;
      let xfId = xfByKey.get(key);
      if (!xfId) {
        xfId = `x:${source.id}:${String(route.transform?.scale ?? 1)}`;
        xfByKey.set(key, xfId);
        if (!nodes.some((node) => node.id === xfId)) {
          nodes.push({
            id: xfId,
            type: 'transform',
            position: posFor(layout, xfId, { x: 520, y: 80 + index * 90 }),
            data: {
              kind: 'transform',
              label: transformLabel(route.transform),
              transform: route.transform || { scale: 1, offset: 0 },
            },
          });
        }
        edges.push({
          id: `e-${xfId}`,
          source: fromId,
          sourceHandle: fromHandle,
          target: xfId,
          targetHandle: 'in',
        });
      }
      fromId = xfId;
      fromHandle = 'out';
    }
    edges.push({
      id: `e-${route.id}`,
      source: fromId,
      sourceHandle: fromHandle,
      target: sink.id,
      targetHandle: sink.handle,
      data: { routeId: route.id },
    });
  });

  attachDashboardWires(nodes, edges, graph, layout);
  attachCloudMqttRoutes(nodes, edges, graph, layout);

  for (const node of nodes) {
    if (layout.nodes?.[node.id]?.generated) {
      node.data.generated = true;
    }
  }

  return { nodes: catalogizeWs2812Nodes(nodes), edges };
}

function attachDashboardWires(nodes: FlowNode[], edges: FlowEdge[], settings: SettingsBag, layout: FlowLayout): void {
  const widgets = asArray(asObject(settings.cloud).widgets).map((item) => asObject(item)).filter((item) => item.id);
  if (!widgets.length) {
    return;
  }
  const pushEdge = (id: string, source: { id: string; handle: string }, target: string, handle: string) => {
    if (edges.some((edge) => edge.id === id || (edge.source === source.id && edge.target === target && String(edge.targetHandle || '') === handle))) {
      return;
    }
    edges.push({
      id,
      source: source.id,
      sourceHandle: source.handle,
      target,
      targetHandle: handle,
    });
  };
  const wireMqttToDash = (mqtt: FlowNode | undefined, sinkId: string, handle: string, widgetId: string) => {
    if (!mqtt || mqtt.data.subscribe === false) {
      return;
    }
    const cloudId = `c:${mqtt.id}`;
    let cloud = nodes.find((node) => node.id === cloudId);
    if (!cloud) {
      cloud = {
        id: cloudId,
        type: 'cloudTransform',
        position: posFor(layout, cloudId, { x: mqtt.position.x + 200, y: mqtt.position.y }),
        data: {
          kind: 'cloudTransform',
          label: 'Cloud',
        },
      };
      nodes.push(cloud);
    }
    pushEdge(`e-${mqtt.id}-cloud`, { id: mqtt.id, handle: 'subscribe' }, cloud.id, 'in');
    pushEdge(`e-cloud-${widgetId}-${handle}`, { id: cloud.id, handle: 'out' }, sinkId, handle);
  };
  widgets.forEach((widget, index) => {
    const dest = `dashboard.${widget.id}`;
    const sinkId = `k:dash:${widget.id}`;
    if (!nodes.some((node) => node.data.kind === 'sink' && node.data.to === dest)) {
      nodes.push({
        id: sinkId,
        type: 'sink',
        position: posFor(layout, sinkId, { x: 1280, y: 40 + index * 80 }),
        data: {
          kind: 'sink',
          label: String(widget.title || widget.id),
          to: dest,
          widgetType: String(widget.type || 'temperature'),
          topic: widget.topic ? String(widget.topic) : undefined,
          topicRh: widget.topicRh ? String(widget.topicRh) : undefined,
          topicPower: widget.topicPower ? String(widget.topicPower) : undefined,
          topicReverse: widget.topicReverse ? String(widget.topicReverse) : undefined,
          signalName: widget.signalName != null ? String(widget.signalName) : undefined,
        },
      });
    }
    const sink = nodes.find((node) => node.data.kind === 'sink' && node.data.to === dest);
    if (!sink) {
      return;
    }
    if (widget.topic && !sink.data.topic) {
      sink.data.topic = String(widget.topic);
    }
    if (widget.topicRh && !sink.data.topicRh) {
      sink.data.topicRh = String(widget.topicRh);
    }
    if (widget.topicPower && !sink.data.topicPower) {
      sink.data.topicPower = String(widget.topicPower);
    }
    if (widget.topicReverse && !sink.data.topicReverse) {
      sink.data.topicReverse = String(widget.topicReverse);
    }
    if (widget.type && !sink.data.widgetType) {
      sink.data.widgetType = String(widget.type);
    }
    if (widget.signalName != null && sink.data.signalName === undefined) {
      sink.data.signalName = String(widget.signalName);
    }
    if (isClimateDashboard(sink.data)) {
      wireMqttToDash(findMqttBrick(nodes, String(widget.topic || sink.data.topic || '')), sink.id, 'temp', String(widget.id));
      wireMqttToDash(findMqttBrick(nodes, String(widget.topicRh || sink.data.topicRh || '')), sink.id, 'rh', String(widget.id));
      return;
    }
    if (isSmlDashboard(sink.data)) {
      wireMqttToDash(findMqttBrick(nodes, String(widget.topic || sink.data.topic || '')), sink.id, 'energy', String(widget.id));
      wireMqttToDash(findMqttBrick(nodes, String(widget.topicPower || sink.data.topicPower || '')), sink.id, 'power', String(widget.id));
      wireMqttToDash(findMqttBrick(nodes, String(widget.topicReverse || sink.data.topicReverse || '')), sink.id, 'reverse', String(widget.id));
      return;
    }
    wireMqttToDash(findMqttBrick(nodes, String(widget.topic || sink.data.topic || '')), sink.id, 'in', String(widget.id));
  });
}

function attachControllerOwnerWires(nodes: FlowNode[], edges: FlowEdge[]): void {
  for (const bus of nodes) {
    const serial = isControllerSerialDevice(bus.data.deviceType);
    const wantsOwner = bus.data.deviceType === 'modbus'
      || bus.data.deviceType === 'i2c'
      || bus.data.deviceType === 'uart'
      || serial
      || isCatalogStacklight(bus.data);
    if (!wantsOwner) {
      continue;
    }
    let ownerId = String(bus.data.ownerId || '');
    if (!ownerId && serial) {
      const controllers = nodes.filter((node) => node.data.kind === 'controller');
      if (controllers.length === 1) {
        ownerId = controllers[0].id;
      }
    }
    if (!ownerId || !nodes.some((node) => node.id === ownerId)) {
      continue;
    }
    if (edges.some((edge) => edge.source === ownerId && edge.target === bus.id)) {
      continue;
    }
    edges.push({
      id: `e-owner-${ownerId}-${bus.id}`,
      source: ownerId,
      sourceHandle: 'out',
      target: bus.id,
      targetHandle: serial ? 'in' : 'owner',
    });
  }
}

function attachSerialReceiveTriggers(nodes: FlowNode[], edges: FlowEdge[], routes: GraphRoute[], layout: FlowLayout): void {
  const targets = new Set<string>();
  routes.forEach((route) => {
    if (!serialEventFrom(String(route.from || ''))) {
      return;
    }
    const source = resolveFromNode(nodes, route.from);
    if (source) {
      targets.add(source.id);
    }
  });
  nodes.forEach((node) => {
    if (isControllerSerialDevice(node.data.deviceType)) {
      targets.add(node.id);
    }
  });
  targets.forEach((targetId) => {
    const target = nodes.find((node) => node.id === targetId);
    if (!target || !isControllerSerialDevice(target.data.deviceType)) {
      return;
    }
    let trigger = nodes.find((node) => (
      node.data.kind === 'trigger'
      && node.data.when === 'event'
      && edges.some((edge) => edge.source === node.id && edge.target === targetId)
    ));
    if (!trigger) {
      trigger = nodes.find((node) => node.data.kind === 'trigger' && node.data.when === 'event');
    }
    if (!trigger) {
      const id = `t:rx:${targetId.replace(/^d:/, '')}`;
      trigger = {
        id,
        type: 'trigger',
        position: posFor(layout, id, { x: 40, y: target.position.y + 140 }),
        data: {
          kind: 'trigger',
          label: layout.nodes?.[id]?.label || 'RX',
          when: 'event',
        },
      };
      nodes.push(trigger);
    }
    if (!edges.some((edge) => edge.source === trigger!.id && edge.target === targetId)) {
      edges.push({
        id: `e-rx-${trigger.id}-${targetId}`,
        source: trigger.id,
        sourceHandle: 'out',
        target: targetId,
        targetHandle: 'rx',
      });
    }
  });
}

function attachStacklightMqttWires(nodes: FlowNode[], edges: FlowEdge[]): void {
  for (const sl of nodes) {
    if (!isCatalogStacklight(sl.data)) {
      continue;
    }
    const topic = String(sl.data.topic || '');
    let mqtt = topic ? findMqttBrick(nodes, topic) : undefined;
    if (!mqtt) {
      const candidates = nodes.filter((node) => (
        node.data.deviceType === 'mqtt'
        && (topic ? mqttBrickTopic(node) === topic : !mqttBrickTopic(node))
        && !edges.some((edge) => edge.source === sl.id && edge.target === node.id)
      ));
      candidates.sort((a, b) => {
        const da = Math.abs(a.position.y - sl.position.y) + Math.abs(a.position.x - sl.position.x);
        const db = Math.abs(b.position.y - sl.position.y) + Math.abs(b.position.x - sl.position.x);
        return da - db;
      });
      mqtt = candidates[0];
    }
    if (!mqtt) {
      continue;
    }
    const brick = mqtt;
    if (edges.some((edge) => edge.source === sl.id && edge.target === brick.id)) {
      continue;
    }
    edges.push({
      id: `e-sl-${sl.id}-${brick.id}`,
      source: sl.id,
      sourceHandle: 'out',
      target: brick.id,
      targetHandle: 'publish',
    });
  }
}

function catalogStripNodeId(nodes: FlowNode[], stripId: string): string | undefined {
  const want = String(stripId || '').trim();
  if (!want) {
    return undefined;
  }
  const match = nodes.find((node) => (
    node.data.deviceType === 'ws2812'
    && (node.data.device?.id === want || node.id === `d:${want}` || node.id === want)
  ));
  return match?.id;
}

function attachStacklightStripWires(nodes: FlowNode[], edges: FlowEdge[], layout: FlowLayout): void {
  for (const sl of nodes) {
    if (!isCatalogStacklight(sl.data)) {
      continue;
    }
    const listed = Array.isArray(sl.data.device?.strips)
      ? (sl.data.device?.strips as unknown[]).map((item) => String(item || '').trim())
      : Array.isArray(layout.nodes?.[sl.id]?.strips)
        ? (layout.nodes?.[sl.id]?.strips || []).map((item) => String(item || '').trim())
        : [];
    for (let i = 0; i < STACKLIGHT_STRIP_COUNT; i += 1) {
      const handle = stacklightStripHandle(i);
      if (edges.some((edge) => edge.target === sl.id && String(edge.targetHandle || '') === handle)) {
        continue;
      }
      const stripNodeId = catalogStripNodeId(nodes, listed[i] || stacklightStripId(i));
      if (!stripNodeId) {
        continue;
      }
      edges.push({
        id: `e-strip-${stripNodeId}-${sl.id}-${i}`,
        source: stripNodeId,
        sourceHandle: 'out',
        target: sl.id,
        targetHandle: handle,
      });
    }
  }
}

function attachCloudMqttRoutes(nodes: FlowNode[], edges: FlowEdge[], settings: SettingsBag, layout: FlowLayout): void {
  const pushEdge = (id: string, source: { id: string; handle: string }, target: string, handle: string) => {
    if (edges.some((edge) => edge.id === id || (edge.source === source.id && edge.target === target && String(edge.targetHandle || '') === handle))) {
      return;
    }
    edges.push({
      id,
      source: source.id,
      sourceHandle: source.handle,
      target,
      targetHandle: handle,
    });
  };
  asArray(asObject(settings.cloud).routes).map((item) => asObject(item)).forEach((raw, index) => {
    const route = raw as GraphRoute;
    const fromTopic = normalizeMqttTopic(String(raw.from || ''));
    const toTopic = mqttTopicOfRoute(route);
    if (!fromTopic || !toTopic) {
      return;
    }
    const fromMqtt = findMqttBrick(nodes, fromTopic);
    const toMqtt = findMqttBrick(nodes, toTopic);
    if (!fromMqtt || !toMqtt || fromMqtt.id === toMqtt.id) {
      return;
    }
    const cloudId = String(raw.id ? `c:mqtt:${raw.id}` : `c:mqtt:${index}`);
    let cloud = nodes.find((node) => node.id === cloudId);
    if (!cloud) {
      const unused = nodes.filter((node) => (
        node.data.kind === 'cloudTransform'
        && !edges.some((edge) => edge.source === node.id || edge.target === node.id)
      ));
      unused.sort((a, b) => Math.abs(a.position.y - fromMqtt.position.y) - Math.abs(b.position.y - fromMqtt.position.y));
      cloud = unused[0];
    }
    if (!cloud) {
      cloud = {
        id: cloudId,
        type: 'cloudTransform',
        position: posFor(layout, cloudId, { x: fromMqtt.position.x + 200, y: fromMqtt.position.y }),
        data: {
          kind: 'cloudTransform',
          label: 'Cloud',
          transform: route.transform,
        },
      };
      nodes.push(cloud);
    } else if (route.transform && !cloud.data.transform) {
      cloud.data.transform = route.transform;
    }
    pushEdge(`e-${fromMqtt.id}-${cloud.id}`, { id: fromMqtt.id, handle: 'subscribe' }, cloud.id, 'in');
    pushEdge(`e-${cloud.id}-${toMqtt.id}`, { id: cloud.id, handle: 'out' }, toMqtt.id, 'publish');
  });
}

function resolveFromNode(nodes: FlowNode[], from: string): { id: string; handle?: string } | null {
  const parts = from.split('.').filter(Boolean);
  if (parts.length === 0) {
    return null;
  }
  const device = nodes.find((n) => n.data.kind === 'device' && n.data.device?.id === parts[0]);
  if (parts.length === 1) {
    return device ? { id: device.id, handle: sourceHandleFor(device.data.deviceType || '') } : null;
  }
  if (parts.length === 2 && device?.data.deviceType === 'sht21') {
    return { id: device.id, handle: parts[1] === 'humidity' || parts[1] === 'rh' ? 'humidity' : 'temperature' };
  }
  if (parts.length === 2 && device?.data.deviceType === 'gps') {
    return { id: device.id, handle: parts[1] };
  }
  if (parts.length === 2 && device?.data.deviceType === 'sml') {
    return { id: device.id, handle: parts[1] };
  }
  if (parts.length === 2 && device?.data.deviceType === 'victron') {
    return { id: device.id, handle: parts[1] };
  }
  if (parts.length === 2 && device?.data.deviceType === 'pn532') {
    return { id: device.id, handle: parts[1] };
  }
  if (parts.length === 2 && device?.data.deviceType === 'vl53l0x') {
    return { id: device.id, handle: parts[1] === 'range' || parts[1] === 'distance' ? 'mm' : parts[1] };
  }
  if (parts.length === 2 && device?.data.deviceType === 'zigbee') {
    return { id: device.id, handle: parts[1] === 'uplink' ? 'publish' : 'downlink' };
  }
  if (parts.length === 2 && device?.data.deviceType === 'modbus_slave') {
    const point = nodes.find(
      (n) => n.data.kind === 'point' && n.data.busId === parts[0] && String(asObject(n.data.point).key) === parts[1],
    );
    if (point) {
      return { id: point.id, handle: 'out' };
    }
  }
  if (parts.length === 2) {
    const named = nodes.find(
      (n) => n.data.kind === 'point' && n.data.busId === parts[0] && String(asObject(n.data.point).key) === parts[1],
    );
    if (named) {
      return { id: named.id, handle: 'out' };
    }
  }
  const point = nodes.find(
    (n) => n.data.kind === 'point' && n.data.busId === parts[0] && String(asObject(n.data.slave).id) === parts[1] && String(asObject(n.data.point).key) === parts[2],
  );
  if (point) {
    return { id: point.id, handle: 'out' };
  }
  const slave = nodes.find(
    (n) => n.data.kind === 'slave' && n.data.busId === parts[0] && String(asObject(n.data.slave).id) === parts[1],
  );
  if (slave) {
    return { id: slave.id, handle: 'out' };
  }
  return device ? { id: device.id, handle: sourceHandleFor(device.data.deviceType || '') } : null;
}

function resolveToNode(nodes: FlowNode[], route: GraphRoute): { id: string; handle?: string } | null {
  const dest = String(route.to || '');
  if (dest.startsWith('lvgl.') || dest.startsWith('modbus.hr') || dest.startsWith('dashboard.')) {
    const sink = nodes.find((n) => n.data.kind === 'sink' && n.data.to === dest);
    return sink ? { id: sink.id, handle: 'in' } : null;
  }
  if (isMqttDest(route)) {
    const mqtt = findMqttBrick(nodes, mqttTopicOfRoute(route));
    return mqtt ? { id: mqtt.id, handle: 'publish' } : null;
  }
  if (dest.startsWith('lorawan')) {
    const lora = nodes.find((n) => n.data.device?.type === 'lorawan');
    return lora ? { id: lora.id, handle: 'publish' } : null;
  }
  if (dest.startsWith('zigbee')) {
    const zb = nodes.find((n) => n.data.device?.type === 'zigbee');
    return zb ? { id: zb.id, handle: 'publish' } : null;
  }
  if (route.to) {
    const [id, handle] = route.to.split('.');
    const node = nodes.find((n) => n.data.kind === 'device' && n.data.device?.id === id);
    return node ? { id: node.id, handle: handle || 'in' } : null;
  }
  const lora = nodes.find((n) => n.data.device?.type === 'lorawan');
  if (lora && route.channel === 'lorawan') {
    return { id: lora.id, handle: 'publish' };
  }
  const zb = nodes.find((n) => n.data.device?.type === 'zigbee');
  if (zb && route.channel === 'zigbee') {
    return { id: zb.id, handle: 'publish' };
  }
  return null;
}

function sourceHandleFor(type: string): string {
  if (type === 'sht21' || type === 'vl53l0x') {
    return 'sample';
  }
  if (type === 'mqtt') {
    return 'subscribe';
  }
  if (type === 'zigbee') {
    return 'downlink';
  }
  if (type === 'modbus') {
    return 'bus';
  }
  return 'out';
}

function fromRef(node: FlowNode, handle?: string | null): string {
  if (node.data.kind === 'point') {
    const slaveId = String(asObject(node.data.slave).id || '');
    const key = String(asObject(node.data.point).key || '');
    if (node.data.busId === slaveId) {
      return `${node.data.busId}.${key}`;
    }
    return `${node.data.busId}.${slaveId}.${key}`;
  }
  if (node.data.kind === 'slave') {
    return `${node.data.busId}.${String(asObject(node.data.slave).id || '')}`;
  }
  const id = node.data.device?.id || '';
  if (node.data.deviceType === 'sht21' && handle && handle !== 'sample' && handle !== 'out') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'gps' && handle && handle !== 'out') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'sml' && handle && handle !== 'out' && handle !== 'in' && handle !== 'poll' && handle !== 'rx') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'victron' && handle && handle !== 'out' && handle !== 'in' && handle !== 'rx') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'pn532' && handle && handle !== 'out' && handle !== 'in') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'vl53l0x' && handle && handle !== 'sample' && handle !== 'out' && handle !== 'in') {
    return `${id}.${handle}`;
  }
  if (node.data.deviceType === 'zigbee') {
    return handle === 'publish' ? 'zigbee.uplink' : 'zigbee.downlink';
  }
  return id;
}

function toRef(node: FlowNode, handle?: string | null): { channel?: string; to?: string; topic?: string } {
  if (node.data.kind === 'sink') {
    const to = String(node.data.to || '');
    if (to.startsWith('dashboard.')) {
      return { to };
    }
    if (to.startsWith('lorawan')) {
      return { channel: 'lorawan', to };
    }
    if (to.startsWith('zigbee')) {
      return { channel: 'zigbee', to };
    }
    if (to.startsWith('mqtt.') || to.startsWith('mqtt:')) {
      return { channel: 'mqtt', to };
    }
    if (to) {
      return { to };
    }
    const widget = String(node.data.label || 'value').split('·').pop()?.trim() || 'value';
    const dest = node.data.layoutId ? `lvgl.${node.data.layoutId}.${widget}` : `lvgl.${widget}`;
    return { to: dest };
  }
  if (node.data.deviceType === 'mqtt') {
    const topic = normalizeMqttTopic(String(node.data.topic || '')) || '{base}/topic';
    return { channel: 'mqtt', topic, to: `mqtt.${topic}` };
  }
  if (node.data.deviceType === 'lorawan') {
    return { channel: 'lorawan' };
  }
  if (node.data.deviceType === 'zigbee') {
    return { channel: 'zigbee', to: 'zigbee.uplink' };
  }
  if (node.data.deviceType === 'gpio_relay') {
    const channel = handle && handle !== 'in' ? handle : '1';
    return { to: `${node.data.device?.id || 'relay'}.${channel}` };
  }
  if (node.data.deviceType === 'pn532') {
    return { to: `${node.data.device?.id}.${handle || 'ndef'}` };
  }
  if (node.data.deviceType === 'ws2812') {
    return { to: `${node.data.device?.id}.${handle || 'fill'}` };
  }
  if (node.data.deviceType === 'stacklight') {
    return { to: `${node.data.device?.id}.${handle || 'color'}` };
  }
  if (node.data.deviceType === 'modbus_slave') {
    return { to: `${node.data.device?.id}${handle && handle !== 'in' ? `.${handle}` : ''}` };
  }
  return { channel: 'mqtt' };
}

function pollOf(
  node: FlowNode,
  handle: string | null | undefined,
  incoming: Map<string, FlowEdge[]>,
  byId: Record<string, FlowNode>,
): { when: string; interval?: number } {
  if (isControllerSerialDevice(node.data.deviceType)) {
    return { when: 'event' };
  }
  const ins = incoming.get(node.id) || [];
  for (const edge of ins) {
    const src = byId[edge.source];
    if (src?.data.kind !== 'trigger') {
      continue;
    }
    const targetHandle = String(edge.targetHandle || '');
    if (node.data.kind === 'point' && (targetHandle === 'poll' || targetHandle === 'in')) {
      return { when: 'interval', interval: Number(src.data.interval) || 60 };
    }
    const want = handle ? `poll:${handle}` : 'poll';
    if (targetHandle === want || targetHandle === 'poll') {
      return { when: 'interval', interval: Number(src.data.interval) || 60 };
    }
  }
  return { when: 'event' };
}

export function flowToSettings(
  nodes: FlowNode[],
  edges: FlowEdge[],
  settings: SettingsBag,
  options?: { routesOnly?: boolean },
): SettingsBag {
  const devices: GraphDevice[] = options?.routesOnly
    ? readDevices(ensureGraph(settings))
    : (() => {
      const built: GraphDevice[] = [];
      for (const prev of readDevices(settings)) {
        if (prev.type !== 'modbus' && prev.type !== 'mqtt') {
          continue;
        }
        const bus = clone(prev);
        delete bus.slaves;
        built.push(bus);
      }
      for (const node of nodes) {
        if (node.data.kind !== 'device' || !node.data.device) {
          continue;
        }
        const device = clone(node.data.device);
        delete device.slaves;
        if (device.type === 'i2c' || device.type === 'uart' || device.type === 'mqtt') {
          continue;
        }
        if (isCatalogStacklight(node.data)) {
          continue;
        }
        if (node.data.deviceType === 'ws2812' && edges.some((edge) => (
          edge.source === node.id
          && nodes.some((item) => item.id === edge.target && isCatalogStacklight(item.data))
        ))) {
          continue;
        }
        if (device.type === 'modbus_slave') {
          device.points = [];
        }
        if (built.some((d) => d.id === device.id)) {
          continue;
        }
        built.push(device);
        if (built.length >= MAX_DEVICES) {
          break;
        }
      }
      for (const node of nodes) {
        if (node.data.kind !== 'slave') {
          continue;
        }
        const raw = asObject(node.data.slave);
        const id = String(raw.id || '');
        if (!id || built.some((d) => d.id === id)) {
          continue;
        }
        built.push(slaveFromLegacy(raw, String(node.data.busId || 'modbus')));
      }
      for (const node of nodes) {
        if (node.data.kind !== 'point') {
          continue;
        }
        const slaveId = String(node.data.busId || asObject(node.data.slave).id || '');
        const slave = built.find((d) => d.id === slaveId && d.type === 'modbus_slave');
        if (!slave) {
          continue;
        }
        const points = rawPoints(slave.points);
        points.push(...rawPoints([node.data.point]));
        slave.points = points;
      }
      return liftModbusSlaves(built).devices;
    })();

  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const incoming = new Map<string, FlowEdge[]>();
  for (const edge of edges) {
    const list = incoming.get(edge.target) || [];
    list.push(edge);
    incoming.set(edge.target, list);
  }

  const routes: GraphRoute[] = [];
  const cloudRoutes: GraphRoute[] = [];
  const usedEdges = new Set<string>();

  const walkSource = (nodeId: string, handle?: string | null): { node: FlowNode; handle?: string | null; transform?: RouteTransform; cloud?: boolean } | null => {
    const node = byId[nodeId];
    if (!node) {
      return null;
    }
    if (node.data.kind === 'transform') {
      const prev = (incoming.get(node.id) || [])[0];
      if (!prev) {
        return { node, handle, transform: node.data.transform };
      }
      usedEdges.add(prev.id);
      const src = walkSource(prev.source, prev.sourceHandle);
      return src ? { ...src, transform: node.data.transform } : null;
    }
    if (node.data.kind === 'cloudTransform') {
      const prev = (incoming.get(node.id) || [])[0];
      if (!prev) {
        return { node, handle, cloud: true, transform: node.data.transform };
      }
      usedEdges.add(prev.id);
      const src = walkSource(prev.source, prev.sourceHandle);
      return src
        ? { ...src, cloud: true, transform: node.data.transform || src.transform }
        : { node, handle, cloud: true, transform: node.data.transform };
    }
    if (node.data.kind === 'trigger') {
      const prev = (incoming.get(node.id) || [])[0];
      if (!prev) {
        return { node, handle };
      }
      usedEdges.add(prev.id);
      return walkSource(prev.source, prev.sourceHandle);
    }
    return { node, handle };
  };

  for (const edge of edges) {
    const target = byId[edge.target];
    if (!target) {
      continue;
    }
    const sourceNode = byId[edge.source];
    if (sourceNode && (
      (flowCategory(sourceNode.data) === 'bus' && sourceNode.data.deviceType !== 'zigbee')
      || sourceNode.data.kind === 'controller'
      || isCatalogStacklight(sourceNode.data)
    )) {
      continue;
    }
    if (isCatalogStacklight(target.data)) {
      continue;
    }
    const destPreview = toRef(target, edge.targetHandle);
    if (isDashboardDest(String(destPreview.to || ''))) {
      continue;
    }
    const isSink =
      target.data.kind === 'sink'
      || (target.data.kind === 'device' && (
        target.data.deviceType === 'mqtt'
        || target.data.deviceType === 'lorawan'
        || target.data.deviceType === 'zigbee'
        || target.data.deviceType === 'ws2812'
        || target.data.deviceType === 'stacklight'
        || target.data.deviceType === 'gpio_relay'
        || target.data.deviceType === 'pn532'
        || target.data.deviceType === 'modbus_slave'
      ));
    if (!isSink) {
      continue;
    }
    usedEdges.add(edge.id);
    const src = walkSource(edge.source, edge.sourceHandle);
    if (src?.cloud && src.node.data.deviceType === 'mqtt' && target.data.deviceType === 'mqtt') {
      const fromTopic = mqttBrickTopic(src.node);
      const dest = destPreview;
      const toTopic = dest.topic || mqttTopicOfRoute({ id: '', from: '', ...dest } as GraphRoute);
      if (fromTopic && toTopic && fromTopic !== toTopic) {
        const existing = edge.data?.routeId;
        const route: GraphRoute = {
          id: existing || `c_${fromTopic.replace(/[^a-zA-Z0-9]+/g, '_')}`.slice(0, 31),
          from: fromTopic,
          channel: 'mqtt',
          topic: toTopic,
          to: dest.to || `mqtt.${toTopic}`,
        };
        if (src.transform && !identityTransform(src.transform)) {
          route.transform = src.transform;
        }
        cloudRoutes.push(route);
      }
      continue;
    }
    if (!src || src.cloud || src.node.data.kind === 'cloudTransform') {
      continue;
    }
    const from = fromRef(src.node, src.handle);
    const dest = destPreview;
    const existing = edge.data?.routeId;
    const xfNode = nodes.find((n) => n.id === edge.source && n.data.kind === 'transform');
    const transform = src.transform || xfNode?.data.transform;
    const poll = pollOf(src.node, src.handle, incoming, byId);
    const prevRoute = readRoutes(settings).find((r) => r.id === existing);
    const route: GraphRoute = {
      id: existing || `r_${from.replace(/[^a-zA-Z0-9]+/g, '_')}_${target.data.device?.id || 'sink'}`.slice(0, 31),
      enable: prevRoute?.enable !== false,
      when: poll.when,
      from,
      ...dest,
    };
    if (poll.interval != null) {
      route.interval = poll.interval;
    }
    if (dest.channel === 'mqtt') {
      route.topic = dest.topic || mqttTopicFrom(from, prevRoute?.topic);
    }
    if (dest.channel === 'lorawan') {
      route.to = prevRoute?.to || 'lorawan.uplink';
    } else if (dest.channel === 'zigbee') {
      route.to = prevRoute?.to || 'zigbee.uplink';
    } else if (dest.to) {
      route.to = dest.to;
    } else if (route.topic) {
      route.to = prevRoute?.to || `mqtt.${route.topic}`;
    }
    if (transform && !identityTransform(transform)) {
      route.transform = transform;
    }
    routes.push(route);
    if (routes.length >= MAX_ROUTES) {
      break;
    }
  }

  const prevRoutes = readRoutes(settings);
  const edgeIds = new Set(routes.map((r) => r.id));
  const sinkTos = new Set(nodes.filter((n) => n.data.kind === 'sink').map((n) => String(n.data.to || '')));
  for (const prev of prevRoutes) {
    if (edgeIds.has(prev.id)) {
      continue;
    }
    const dest = String(prev.to || '');
    if ((dest.startsWith('lvgl.') || dest.startsWith('modbus.hr')) && !sinkTos.has(dest)) {
      routes.push(prev);
      if (routes.length >= MAX_ROUTES) {
        break;
      }
    }
  }

  const layout: FlowLayout = { nodes: {} };
  for (const node of nodes) {
    const snap = nodeSnapshot(node);
    if (node.data.deviceType === 'mqtt') {
      const subscribed = edges.some((edge) => (
        edge.source === node.id && (edge.sourceHandle === 'subscribe' || edge.sourceHandle === 'message')
      ));
      if (subscribed) {
        delete snap.subscribe;
      }
    }
    if (node.data.deviceType === 'modbus' || node.data.deviceType === 'i2c' || node.data.deviceType === 'uart' || isControllerSerialDevice(node.data.deviceType) || isCatalogStacklight(node.data)) {
      const owner = edges.find((edge) => edge.target === node.id && byId[edge.source]?.data.kind === 'controller');
      if (owner) {
        snap.ownerId = owner.source;
      }
    }
    if (isCatalogStacklight(node.data) && !snap.topic) {
      const mqttEdge = edges.find((edge) => edge.source === node.id && byId[edge.target]?.data.deviceType === 'mqtt');
      const topic = mqttEdge ? mqttBrickTopic(byId[mqttEdge.target]) : '';
      if (topic) {
        snap.topic = topic;
      }
    }
    if (isCatalogStacklight(node.data)) {
      snap.strips = Array.from({ length: STACKLIGHT_STRIP_COUNT }, (_, index) => {
        const handle = stacklightStripHandle(index);
        const incomingStrip = edges.find((edge) => edge.target === node.id && String(edge.targetHandle || '') === handle);
        const src = incomingStrip ? byId[incomingStrip.source] : undefined;
        return String(src?.data.device?.id || '').trim();
      });
    }
    layout.nodes![node.id] = snap;
  }

  const topicFromDashHandle = (nodeId: string, handle: string): string | undefined => {
    const ins = (incoming.get(nodeId) || []).filter((edge) => String(edge.targetHandle || 'in') === handle);
    for (const edge of ins) {
      const seen = new Set<string>();
      let cur: FlowNode | undefined = byId[edge.source];
      while (cur && !seen.has(cur.id)) {
        seen.add(cur.id);
        if (cur.data.deviceType === 'mqtt') {
          const topic = mqttBrickTopic(cur);
          return topic || undefined;
        }
        if (cur.data.kind === 'cloudTransform' || cur.data.kind === 'transform') {
          const prev: FlowEdge | undefined = (incoming.get(cur.id) || [])[0];
          if (!prev) {
            break;
          }
          cur = byId[prev.source];
          continue;
        }
        break;
      }
    }
    return undefined;
  };

  const findUpstream = (startId: string, pred: (node: FlowNode) => boolean): FlowNode | undefined => {
    const seen = new Set<string>();
    const queue = [startId];
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id)) {
        continue;
      }
      seen.add(id);
      const node = byId[id];
      if (!node) {
        continue;
      }
      if (pred(node)) {
        return node;
      }
      for (const edge of incoming.get(id) || []) {
        queue.push(edge.source);
      }
    }
    return undefined;
  };

  const cloudWidgets = nodes
    .filter((node) => {
      if (node.data.kind !== 'sink' || !String(node.data.to || '').startsWith('dashboard.')) {
        return false;
      }
      return (incoming.get(node.id) || []).length > 0;
    })
    .map((node) => {
      const id = String(node.data.to || '').slice('dashboard.'.length);
      const stacklight = isStacklightDashboard(node.data);
      const widget: Record<string, unknown> = {
        id,
        type: stacklight ? 'stacklight' : (node.data.widgetType || 'temperature'),
        title: node.data.label,
      };
      const bound = findUpstream(node.id, (item) => isCatalogStacklight(item.data));
      const signalName = node.data.signalName || bound?.data.signalName;
      if (signalName) {
        widget.signalName = signalName;
      }
      const topic = node.data.topic
        || topicFromDashHandle(node.id, stacklight ? 'in' : (isSmlDashboard(node.data) ? 'energy' : 'temp'))
        || topicFromDashHandle(node.id, 'in')
        || bound?.data.topic;
      const topicRh = stacklight ? undefined : (node.data.topicRh || topicFromDashHandle(node.id, 'rh'));
      const topicPower = node.data.topicPower || topicFromDashHandle(node.id, 'power');
      const topicReverse = node.data.topicReverse || topicFromDashHandle(node.id, 'reverse');
      if (topic) {
        widget.topic = topic;
      }
      if (topicRh) {
        widget.topicRh = topicRh;
      }
      if (topicPower) {
        widget.topicPower = topicPower;
      }
      if (topicReverse) {
        widget.topicReverse = topicReverse;
      }
      return widget;
    })
    .filter((widget) => widget.topic || widget.signalName);

  const next: SettingsBag = {
    ...settings,
    devices,
    routes,
    flow: layout,
  };
  const cloudBag: SettingsBag = { ...asObject(settings.cloud) };
  if (cloudWidgets.length) {
    cloudBag.widgets = cloudWidgets;
  } else {
    delete cloudBag.widgets;
  }
  if (cloudRoutes.length) {
    cloudBag.routes = cloudRoutes;
  } else {
    delete cloudBag.routes;
  }
  if (cloudWidgets.length || cloudRoutes.length) {
    next.cloud = cloudBag;
  } else {
    delete next.cloud;
  }
  return projectDevicesToGroups(next);
}

export function applyNodePosition(settings: SettingsBag, nodeId: string, x: number, y: number): SettingsBag {
  const flow = readFlowLayout(settings);
  flow.nodes = { ...(flow.nodes || {}), [nodeId]: { x, y } };
  return { ...settings, flow };
}
